<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useConfig } from 'dashboard/composables/useConfig';
import FunnelAPI from '../../../../api/funnel';
import StageColorPicker from './StageColorPicker.vue';
import draggable from 'vuedraggable';
import agents from '../../../../api/agents';
import inboxesAPI from '../../../../api/inboxes';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

import { reactive } from 'vue';

const props = defineProps({
  isEditing: {
    type: Boolean,
    default: false,
  },
  funnelId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(['saved', 'close', 'metadata-updated']);
const { t } = useI18n();
const { isStacklab } = useConfig();
const loading = ref(false);
const errorMessage = ref('');
const leftColumnCollapsed = ref(false);
const globalFieldsCollapsed = ref(false);
const stagesCollapsed = ref(false);
const agentsCollapsed = ref(false);
const templatesCollapsed = ref(false);

const formData = ref({
  name: '',
  description: '',
  active: true,
  stages: {},
  settings: {},
  agents: [],
  global_custom_attributes: [],
});

// Campos adicionais para exibição
const funnelMetadata = ref({
  updated_at: null,
  optimization_history: [],
});

const stages = ref([]);
const newStage = ref({
  name: '',
  color: '#FF6B6B',
  description: '',
  auto_create_conditions: [],
});

const editingStage = ref(null);

const dragOptions = {
  animation: 150,
  ghostClass: 'ghost-card',
};

const vFocus = {
  mounted: el => el.focus(),
};

const agentsList = ref([]);
const loadingAgents = ref(false);
const existingStageIds = ref(new Set());
const stageStats = ref({});
const loadingStats = ref(false);

const newCondition = ref({
  type: 'contact_has_tag',
  value: '',
  attribute: '',
  operator: 'equal_to',
});


// Gera o ID baseado no nome
const generateId = name => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

// Gera um ID único apenas se necessário
const generateUniqueId = (baseName, existingStages, currentId = null) => {
  // Se já tem um ID e está editando, mantém o mesmo
  if (currentId && editingStage.value) {
    return currentId;
  }

  const baseId = generateId(baseName);
  const stageIds = existingStages.map(stage => stage.id);

  // Se o ID base não existe, usa ele
  if (!stageIds.includes(baseId)) {
    return baseId;
  }

  // Se existe, adiciona um timestamp para garantir unicidade
  return `${baseId}_${Date.now()}`;
};

// Atualiza o ID apenas para novas etapas
watch(
  () => newStage.value.name,
  newName => {
    if (newName && !editingStage.value) {
      newStage.value.id = generateId(newName);
    }
  }
);

// Converte as etapas para o formato esperado pela API
const formatStagesForAPI = () => {
  const formattedStages = {};

  // Garantir que as posições reflitam a ordem atual
  stages.value.forEach((stage, index) => {
    // Definir explicitamente a posição baseada no índice atual
    const position = index + 1;

    // Pega os templates existentes da etapa atual
    const existingTemplates =
      formData.value.stages?.[stage.id]?.message_templates || [];

    formattedStages[stage.id] = {
      name: stage.name,
      color: stage.color,
      position: position, // Garantir que a posição reflita a ordem atual
      description: stage.description,
      auto_create_conditions: stage.auto_create_conditions || [],
      message_templates: existingTemplates, // Mantém os templates existentes
    };
  });

  return formattedStages;
};

// Função para lidar com a reordenação das etapas
const handleOrderChange = async () => {
  if (!props.isEditing) return;

  try {
    // Atualizar as posições com base na nova ordem
    stages.value.forEach((stage, index) => {
      stage.position = index + 1;
    });

    const payload = {
      name: formData.value.name,
      description: formData.value.description,
      stages: formatStagesForAPI(),
    };

    // Atualiza o estado local sem fazer chamada à API
    formData.value.stages = payload.stages;
  } catch (error) {
    // Error handling
  }
};

const handleAddStage = e => {
  if (e?.target?.closest('.colorpicker--chrome')) {
    return;
  }

  if (!newStage.value.name) return;

  if (editingStage.value) {
    const index = stages.value.findIndex(s => s.id === editingStage.value.id);
    if (index !== -1) {
      stages.value[index] = {
        ...editingStage.value,
        name: newStage.value.name,
        color: newStage.value.color,
        description: newStage.value.description,
        auto_create_conditions: newStage.value.auto_create_conditions,
        id: editingStage.value.id,
      };
      if (props.isEditing) {
        handleOrderChange();
      }
    }
    editingStage.value = null;
  } else {
    let stageId = generateId(newStage.value.name);
    let counter = 1;
    while (existingStageIds.value.has(stageId)) {
      stageId = `${generateId(newStage.value.name)}_${counter}`;
      counter++;
    }
    stages.value.push({
      ...newStage.value,
      id: stageId,
    });
    existingStageIds.value.add(stageId);
  }
  newStage.value = {
    name: '',
    color: '#FF6B6B',
    description: '',
    auto_create_conditions: [],
  };
};

const removeStage = index => {
  stages.value.splice(index, 1);
};

// Busca todos os funis e coleta os IDs existentes
const fetchExistingStageIds = async () => {
  try {
    const { data: allFunnels } = await FunnelAPI.get();
    existingStageIds.value.clear();

    allFunnels.forEach(funnel => {
      if (funnel.id !== props.funnelId) {
        // Ignora o funil atual se estiver editando
        Object.keys(funnel.stages || {}).forEach(id => {
          existingStageIds.value.add(id);
        });
      }
    });
  } catch (error) {
    // Error handling
  }
};

// Inicializa o formulário
onMounted(async () => {
  // Carrega agentes e inboxes sempre
  await Promise.all([fetchAgents(), fetchInboxes()]);

  // Carrega dados do funil se estiver editando
  if (props.isEditing && props.funnelId) {
    await fetchFunnelData();
    await fetchStageStats();
  }

  // Garante que sempre tenha pelo menos um campo customizado vazio
  if (
    !Array.isArray(formData.value.global_custom_attributes) ||
    formData.value.global_custom_attributes.length === 0
  ) {
    formData.value.global_custom_attributes = [{ name: '', type: '', field_type: 'single' }];
  }

  // Adiciona listener para evento personalizado de salvar
  const formElement = document.querySelector('.funnel-form');
  if (formElement) {
    formElement.addEventListener('save-form', handleSubmit);
  }

  // Cleanup do listener quando o componente for desmontado
  onUnmounted(() => {
    if (formElement) {
      formElement.removeEventListener('save-form', handleSubmit);
    }
  });
});

const fetchAgents = async () => {
  try {
    loadingAgents.value = true;
    const { data } = await agents.get();
    agentsList.value = data;
  } catch (error) {
    // Error handling
  } finally {
    loadingAgents.value = false;
  }
};

const fetchInboxes = async () => {
  try {
    loadingInboxes.value = true;
    const response = await inboxesAPI.get();
    inboxes.value = response.data.payload || [];
  } catch (error) {
    inboxes.value = [];
  } finally {
    loadingInboxes.value = false;
  }
};

const fetchStageStats = async () => {
  if (!props.isEditing || !props.funnelId) return;

  try {
    loadingStats.value = true;
    const response = await FunnelAPI.getStageStats(props.funnelId);
    stageStats.value = response.data.stages || {};
  } catch (error) {
    console.error('Erro ao buscar estatísticas:', error);
    stageStats.value = {};
  } finally {
    loadingStats.value = false;
  }
};

const fetchFunnelData = async () => {
  if (!props.isEditing || !props.funnelId) return;

  try {
    loading.value = true;
    const response = await FunnelAPI.getById(props.funnelId);
    const funnelData = response.data;

    // Definir cada propriedade individualmente para garantir reatividade
    formData.value.name = funnelData.name || '';
    formData.value.description = funnelData.description || '';
    formData.value.active = funnelData.active !== undefined ? funnelData.active : true;
    formData.value.stages = funnelData.stages || {};
    formData.value.settings = funnelData.settings || {};
    formData.value.agents = funnelData.settings?.agents || [];
    formData.value.global_custom_attributes = Array.isArray(funnelData.global_custom_attributes)
      ? funnelData.global_custom_attributes.map(attr => ({
          name: attr.name || '',
          type: attr.type || '',
          field_type: attr.is_list ? 'list' : 'single',
        }))
      : [];


    // Armazenar metadados para exibição
    funnelMetadata.value = {
      updated_at: funnelData.updated_at,
      optimization_history: funnelData.settings?.optimization_history || [],
    };

    // Emitir metadados para o header
    emit('metadata-updated', {
      updated_at: funnelData.updated_at,
      name: funnelData.name,
    });

    // Se tiver agentes pré-selecionados e eles não estiverem na lista, adiciona-os
    if (funnelData.settings?.agents?.length) {
      funnelData.settings.agents.forEach(agent => {
        if (!agentsList.value.find(a => a.id === agent.id)) {
          agentsList.value.push(agent);
        }
      });
    }

    // Inicializa as etapas do template
    if (funnelData.stages) {
      stages.value = Object.entries(funnelData.stages)
        .map(([id, stage]) => ({
          id,
          name: stage.name,
          color: stage.color,
          description: stage.description,
          position: parseInt(stage.position, 10) || 0,
          auto_create_conditions: stage.auto_create_conditions || [],
        }))
        .sort((a, b) => a.position - b.position);
    }
  } catch (error) {
    errorMessage.value = 'Erro ao carregar dados do funil';
  } finally {
    loading.value = false;
  }
};

// Funções para gerenciar condições
const addCondition = () => {
  const conditionlessTypes = ['message_not_read', 'conversation_unassigned', 'conversation_reopened', 'conversation_snoozed'];
  if (!conditionlessTypes.includes(newCondition.value.type) && (!newCondition.value.value || newCondition.value.value.toString().trim() === '')) return;

  const condition = { ...newCondition.value };

  // Para condições que não precisam de operador, remove o campo
  if (conditionlessTypes.includes(newCondition.value.type)) {
    delete condition.operator;
    delete condition.attribute;
  }

  newStage.value.auto_create_conditions.push(condition);

  // Reset do formulário de condição
  newCondition.value = {
    type: 'contact_has_tag',
    value: '',
    attribute: '',
    operator: 'equal_to',
  };
};

const removeCondition = index => {
  newStage.value.auto_create_conditions.splice(index, 1);
};

const getConditionTypeLabel = type => {
  const labels = {
    contact_has_tag: 'Contato tem tag',
    contact_has_custom_attribute: 'Atributo customizado',
    message_contains: 'Mensagem contém',
    conversation_has_priority: 'Prioridade da conversa',
    inbox_matches: 'Inbox específico',
    message_is_private: 'Mensagem privada contém',
    message_has_automation: 'Mensagem por automação contém',
    conversation_message_count: 'Quantidade de mensagens',
    last_message_age: 'Idade da última mensagem',
    message_not_read: 'Mensagem não lida',
    conversation_unassigned: 'Conversa sem agente',
    conversation_reopened: 'Conversa reaberta',
    conversation_snoozed: 'Conversa adiada',
  };
  return labels[type] || type;
};

const getOperatorLabel = operator => {
  const labels = {
    equal_to: 'Igual a',
    contains: 'Contém',
    not_equal_to: 'Diferente de',
    greater_than: 'Maior que',
    less_than: 'Menor que',
    greater_than_or_equal: 'Maior ou igual a',
    less_than_or_equal: 'Menor ou igual a',
  };
  return labels[operator] || operator;
};

// Funções para manipular campos personalizados globais
const globalCustomAttributes = computed({
  get: () => formData.value.global_custom_attributes,
  set: val => {
    formData.value.global_custom_attributes = val;
  },
});

function addGlobalCustomAttribute() {
  globalCustomAttributes.value.push({ name: '', type: '', field_type: 'single' });
}

function removeGlobalCustomAttribute(index) {
  globalCustomAttributes.value.splice(index, 1);
}

const handleSubmit = async () => {
  try {
    loading.value = true;
    errorMessage.value = '';

    // Verifica o limite de funis para versão não PRO
    if (!isStacklab && !props.isEditing) {
      const { data: existingFunnels } = await FunnelAPI.get();
      if (existingFunnels.length >= 2) {
        errorMessage.value = t('KANBAN.FUNNELS.FORM.ERROR_LIMIT_REACHED');
        return;
      }
    }

    // Preparar as etapas mantendo os templates existentes e atualizando posições
    const formattedStages = formatStagesForAPI();

    const payload = {
      name: formData.value.name,
      description: formData.value.description,
      active: formData.value.active,
      stages: formattedStages,
      settings: {
        agents: formData.value.agents,
      },
      global_custom_attributes: formData.value.global_custom_attributes
        .filter(attr => attr.name.trim() !== '' && attr.type.trim() !== '')
        .map(attr => ({
          name: attr.name,
          type: attr.type,
          is_list: attr.field_type === 'list'
        })),
    };

    let response;
    if (props.isEditing) {
      // Se estiver editando, faz update na API
      response = await FunnelAPI.update(props.funnelId, payload);
    } else {
      response = await FunnelAPI.create(payload);
    }

    if (response.data) {
      emit('saved', response.data);
    }
  } catch (error) {
    if (error.response?.data?.code === 'FUNNEL_LIMIT_REACHED') {
      errorMessage.value = error.response.data.error;
    } else {
      errorMessage.value = t('KANBAN.FUNNELS.FORM.ERROR_GENERIC');
    }
  } finally {
    loading.value = false;
  }
};

const startEditing = stage => {
  newStage.value = {
    id: stage.id,
    name: stage.name,
    color: stage.color,
    description: stage.description,
    auto_create_conditions: stage.auto_create_conditions || [],
  };
  editingStage.value = stage;
};

const saveStageEdit = index => {
  if (!editingStage.value) return;

  stages.value[index] = {
    ...stages.value[index],
    name: editingStage.value.name,
    description: editingStage.value.description,
    color: editingStage.value.color,
    auto_create_conditions: editingStage.value.auto_create_conditions,
  };

  editingStage.value = null;

  // Se estiver editando, atualiza imediatamente
  if (props.isEditing) {
    handleOrderChange();
  }
};

const cancelEditing = () => {
  editingStage.value = null;
  // Limpa o formulário de nova etapa
  newStage.value = {
    name: '',
    color: '#FF6B6B',
    description: '',
    auto_create_conditions: [],
  };
};

// Watch para agentes
watch(
  () => formData.value.agents,
  newAgents => {
    // Handle agents update
  }
);

const toggleAgent = agent => {
  const index = formData.value.agents.findIndex(a => a.id === agent.id);
  if (index === -1) {
    formData.value.agents.push(agent);
  } else {
    formData.value.agents.splice(index, 1);
  }
};

// Computed para validar se pode adicionar condição
const canAddCondition = computed(() => {
  const conditionlessTypes = ['message_not_read', 'conversation_unassigned', 'conversation_reopened', 'conversation_snoozed'];
  if (conditionlessTypes.includes(newCondition.value.type)) {
    return true;
  }
  return newCondition.value.value && newCondition.value.value.toString().trim() !== '';
});

// Watch para monitorar mudanças nas etapas
watch(
  () => stages.value,
  newStages => {
    // Handle stages update
  },
  { deep: true }
);

watch(
  () => formData.value,
  newFormData => {
    // Handle formData update
  },
  { deep: true }
);

  watch(
    () => formData.value.global_custom_attributes,
    val => {
      if (!Array.isArray(val) || val.length === 0) {
        formData.value.global_custom_attributes = [{ name: '', type: '', field_type: 'single' }];
      }
    },
    { immediate: true }
  );

const agentSearch = ref('');
const agentDropdownOpen = ref(false);
const agentSelectorRef = ref(null);
const filteredAgents = computed(() => {
  if (!agentSearch.value)
    return agentsList.value.filter(
      a => !formData.value.agents.some(sel => sel.id === a.id)
    );
  return agentsList.value.filter(
    a =>
      !formData.value.agents.some(sel => sel.id === a.id) &&
      a.name.toLowerCase().includes(agentSearch.value.toLowerCase())
  );
});

const inboxes = ref([]);
const loadingInboxes = ref(false);
function addAgent(agent) {
  if (!formData.value.agents.some(a => a.id === agent.id))
    formData.value.agents.push(agent);
  agentDropdownOpen.value = false;
  agentSearch.value = '';
}
function removeAgent(agent) {
  formData.value.agents = formData.value.agents.filter(a => a.id !== agent.id);
}
function closeAgentDropdown(e) {
  if (!agentSelectorRef.value?.contains(e.target)) {
    agentDropdownOpen.value = false;
    window.removeEventListener('click', closeAgentDropdown);
  }
}
function openAgentDropdown() {
  agentDropdownOpen.value = true;
  setTimeout(() => {
    window.removeEventListener('click', closeAgentDropdown);
    window.addEventListener('click', closeAgentDropdown);
  }, 0);
}

// Controle de tooltip de cópia por etapa
const copiedStageId = ref({});
const showHelpPopover = ref(false);

// Função para copiar o id da etapa
async function copyStageId(id) {
  navigator.clipboard.writeText(id);
  copiedStageId.value[id] = true;
  await nextTick();
  setTimeout(() => {
    copiedStageId.value[id] = false;
  }, 1500);
}

// Controle de expansão/colapso das etapas na pré-visualização

// Função para formatar valores monetários
const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);
};

// Função para calcular total de templates de mensagem
const getTotalMessageTemplates = () => {
  return stages.value.reduce((total, stage) => {
    return total + ((stage.message_templates || []).length);
  }, 0);
};

// Função para alternar o estado da coluna esquerda
const toggleLeftColumn = () => {
  leftColumnCollapsed.value = !leftColumnCollapsed.value;
};

// Função para alternar o estado dos campos globais
const toggleGlobalFields = () => {
  globalFieldsCollapsed.value = !globalFieldsCollapsed.value;
};

// Função para alternar o estado das etapas
const toggleStages = () => {
  if (stagesCollapsed.value) {
    // Se está colapsado e vai expandir, colapsa os outros
    agentsCollapsed.value = true;
    templatesCollapsed.value = true;
  }
  stagesCollapsed.value = !stagesCollapsed.value;
};

// Função para alternar o estado dos agentes
const toggleAgents = () => {
  if (agentsCollapsed.value) {
    // Se está colapsado e vai expandir, colapsa os outros
    stagesCollapsed.value = true;
    templatesCollapsed.value = true;
  }
  agentsCollapsed.value = !agentsCollapsed.value;
};

// Função para alternar o estado dos templates
const toggleTemplates = () => {
  if (templatesCollapsed.value) {
    // Se está colapsado e vai expandir, colapsa os outros
    stagesCollapsed.value = true;
    agentsCollapsed.value = true;
  }
  templatesCollapsed.value = !templatesCollapsed.value;
};
</script>

<template>
  <form class="funnel-form scrollbar-hide" @submit.prevent="handleSubmit">
    <!-- Mensagem de erro -->
    <div
      v-if="errorMessage"
      class="mb-4 p-4 bg-ruby-50 dark:bg-ruby-900/20 border border-ruby-200 dark:border-ruby-800 rounded-lg"
    >
      <div class="flex items-center gap-2">
        <fluent-icon
          icon="error-circle"
          size="20"
          class="text-ruby-500 dark:text-ruby-300"
        />
        <span class="text-ruby-700 dark:text-ruby-200">{{ errorMessage }}</span>
      </div>
    </div>


    <div class="grid grid-cols-1 md:grid-cols-[1fr_400px] gap-6 md:gap-6 gap-y-6">
      <!-- Coluna Esquerda - Formulário -->
      <div class="space-y-6">
        <!-- Toggle e Título -->
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-2 text-base font-medium">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-file-text-icon lucide-file-text">
              <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/>
              <path d="M14 2v4a2 2 0 0 0 2 2h4"/>
              <path d="M10 9H8"/>
              <path d="M16 13H8"/>
              <path d="M16 17H8"/>
            </svg>
            Dados Básicos
          </div>
          <div class="flex-1 mx-4 h-px bg-slate-200 dark:bg-slate-700"></div>
          <button
            type="button"
            @click="toggleLeftColumn"
            class="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            <fluent-icon
              :icon="leftColumnCollapsed ? 'chevron-down' : 'chevron-up'"
              size="16"
            />
          </button>
        </div>

        <!-- Conteúdo colapsável -->
        <div v-show="!leftColumnCollapsed">

          <!-- Status Ativo -->
          <div>
            <div class="flex items-center justify-between">
              <label class="block text-sm font-medium">
                {{ t('KANBAN.FUNNELS.FORM.ACTIVE.LABEL') }}
              </label>
              <Switch
                v-model="formData.active"
                size="small"
              />
            </div>
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-1 mb-4">
              {{ t('KANBAN.FUNNELS.FORM.ACTIVE.HELP') }}
            </p>
          </div>

          <!-- Nome do Funil -->
          <div>
            <label class="block text-sm font-medium mb-2">
              {{ t('KANBAN.FUNNELS.FORM.NAME.LABEL') }}
            </label>
            <input
              v-model="formData.name"
              type="text"
              class="w-full px-3 py-2 border rounded-lg"
              :placeholder="t('KANBAN.FUNNELS.FORM.NAME.PLACEHOLDER')"
              required
            />
          </div>

          <!-- Descrição -->
          <div>
            <label class="block text-sm font-medium mb-2">
              {{ t('KANBAN.FUNNELS.FORM.DESCRIPTION.LABEL') }}
            </label>
            <textarea
              v-model="formData.description"
              rows="3"
              class="w-full px-3 py-2 border rounded-lg"
              :placeholder="t('KANBAN.FUNNELS.FORM.DESCRIPTION.PLACEHOLDER')"
            />
          </div>



          <!-- Formulário Nova Etapa -->
          <div
            class="border border-slate-100 dark:border-slate-800 rounded-lg p-4 space-y-4"
            :class="{
              'border-2': editingStage
            }"
            :style="editingStage ? { borderColor: newStage.color } : {}"
          >
          <h4 class="text-lg font-medium mb-4">
            <div class="flex items-center justify-between">
              <span>{{ editingStage ? `Editando etapa: ${newStage.name}` : 'Crie uma nova etapa' }}</span>
              <span
                v-if="editingStage"
                class="text-sm px-2 py-1 rounded"
                :style="{
                  backgroundColor: `${newStage.color}20`,
                  color: newStage.color,
                }"
              >
                Editando
              </span>
            </div>
          </h4>
          <!-- Nome da Etapa -->
          <div>
            <label class="block text-sm font-medium mb-2">
              Nome da etapa
            </label>
            <input
              v-model="newStage.name"
              type="text"
              class="w-full px-3 py-2 border rounded-lg"
              placeholder="Nome da etapa"
            />
          </div>
          <!-- Cor e Descrição em flex -->
          <div class="flex items-start gap-2">
            <div class="w-[180px] shrink-0">
              <label class="block text-sm font-medium mb-2"> Cor </label>
              <StageColorPicker v-model="newStage.color" />
            </div>
            <div class="flex-1 min-w-0">
              <label class="block text-sm font-medium mb-2"> Descrição </label>
              <input
                v-model="newStage.description"
                type="text"
                class="w-full px-3 py-2 border rounded-lg"
                placeholder="Descrição da etapa"
              />
            </div>
          </div>
          <!-- Condições de Auto Criação -->
          <div class="mt-4">
            <label class="flex items-center justify-between text-sm font-medium mb-2">
              <span>Condições de Auto Criação</span>
              <div class="relative">
                <span
                  class="text-xs text-slate-400 ml-1 cursor-help"
                  @mouseenter="showHelpPopover = true"
                  @mouseleave="showHelpPopover = false"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 16v-4"/>
                    <path d="M12 8h.01"/>
                  </svg>
                </span>
                <div
                  v-show="showHelpPopover"
                  class="absolute right-0 top-full mt-2 w-80 p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg text-xs text-slate-600 dark:text-slate-300 z-50"
                  @mouseenter="showHelpPopover = true"
                  @mouseleave="showHelpPopover = false"
                >
                  <div class="font-medium text-slate-700 dark:text-slate-200 mb-1">{{ t('KANBAN.FUNNELS.FORM.AUTO_CREATION.HELP_TITLE') }}</div>
                  <p class="leading-relaxed">
                    {{ t('KANBAN.FUNNELS.FORM.AUTO_CREATION.HELP_DESCRIPTION') }}
                  </p>
                </div>
              </div>
            </label>

            <!-- Lista de condições existentes -->
            <div
              v-if="newStage.auto_create_conditions.length > 0"
              class="mb-3 space-y-2"
            >
              <div
                v-for="(condition, index) in newStage.auto_create_conditions"
                :key="index"
                class="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-800 rounded border"
              >
                <span
                  class="text-xs px-2 py-1 bg-woot-100 dark:bg-woot-900 text-woot-700 dark:text-woot-300 rounded"
                >
                  {{ getConditionTypeLabel(condition.type) }}
                </span>
                <span
                  v-if="condition.attribute"
                  class="text-xs text-slate-600 dark:text-slate-400"
                >
                  {{ condition.attribute }}
                </span>
                <span
                  v-if="
                    condition.operator &&
                    condition.type === 'contact_has_custom_attribute'
                  "
                  class="text-xs text-slate-600 dark:text-slate-400"
                >
                  {{ getOperatorLabel(condition.operator) }}
                </span>
                <span class="text-xs font-medium">{{ condition.value }}</span>
                <button
                  type="button"
                  @click="removeCondition(index)"
                  class="ml-auto text-ruby-500 hover:text-ruby-600"
                >
                  ✕
                </button>
              </div>
            </div>

            <!-- Formulário para adicionar nova condição -->
            <div class="flex items-center gap-2">
              <select
                v-model="newCondition.type"
                class="px-2 py-1 border rounded text-sm w-40 h-10 align-middle"
              >
                <option value="contact_has_tag">Contato tem tag</option>
                <option value="contact_has_custom_attribute">
                  Atributo customizado
                </option>
                <option value="message_contains">Mensagem contém</option>
                <option value="conversation_has_priority">
                  Prioridade da conversa
                </option>
                <option value="inbox_matches">Inbox específico</option>
                <option value="message_is_private">Mensagem privada contém</option>
                <option value="message_has_automation">Mensagem por automação contém</option>
                <option value="conversation_message_count">Quantidade de mensagens</option>
                <option value="last_message_age">Idade da última mensagem</option>
                <option value="message_not_read">Mensagem não lida</option>
                <option value="conversation_unassigned">Conversa sem agente</option>
                <option value="conversation_reopened">Conversa reaberta</option>
                <option value="conversation_snoozed">Conversa adiada</option>
              </select>
              <input
                v-if="newCondition.type === 'contact_has_custom_attribute'"
                v-model="newCondition.attribute"
                type="text"
                placeholder="Atributo"
                class="px-2 py-1 border rounded text-sm w-40 h-10 align-middle"
              />
              <select
                v-if="newCondition.type === 'conversation_message_count' || newCondition.type === 'last_message_age'"
                v-model="newCondition.operator"
                class="px-2 py-1 border rounded text-sm w-40 h-10 align-middle"
              >
                <option value="equal_to">Igual a</option>
                <option value="greater_than">Maior que</option>
                <option value="less_than">Menor que</option>
                <option value="greater_than_or_equal">Maior ou igual a</option>
                <option value="less_than_or_equal">Menor ou igual a</option>
              </select>
              <select
                v-if="newCondition.type === 'contact_has_custom_attribute'"
                v-model="newCondition.operator"
                class="px-2 py-1 border rounded text-sm w-40 h-10 align-middle"
              >
                <option value="equal_to">Igual a</option>
                <option value="contains">Contém</option>
                <option value="not_equal_to">Diferente de</option>
              </select>
              <input
                v-if="newCondition.type !== 'inbox_matches' && newCondition.type !== 'message_not_read' && newCondition.type !== 'conversation_unassigned' && newCondition.type !== 'conversation_reopened' && newCondition.type !== 'conversation_snoozed'"
                v-model="newCondition.value"
                :type="newCondition.type === 'conversation_message_count' || newCondition.type === 'last_message_age' ? 'number' : 'text'"
                :placeholder="
                  newCondition.type === 'contact_has_tag'
                    ? 'Tag'
                    : newCondition.type === 'message_contains'
                    ? 'Texto'
                    : newCondition.type === 'message_is_private'
                    ? 'Texto na mensagem privada'
                    : newCondition.type === 'message_has_automation'
                    ? 'Texto na mensagem'
                    : newCondition.type === 'conversation_has_priority'
                    ? 'Prioridade'
                    : newCondition.type === 'conversation_message_count'
                    ? 'Quantidade'
                    : newCondition.type === 'last_message_age'
                    ? 'Minutos'
                    : 'Valor'
                "
                class="px-2 py-1 border rounded text-sm w-40 h-10 align-middle"
              />
              <select
                v-else
                v-model="newCondition.value"
                class="w-full px-3 py-2 border rounded-lg text-sm"
              >
                <option value="">Selecione uma inbox</option>
                <option
                  v-for="inbox in inboxes"
                  :key="inbox.id"
                  :value="inbox.id"
                >
                  {{ inbox.name }} ({{ inbox.channel_type.replace('Channel::', '') }})
                </option>
              </select>
              <Button
                color="blue"
                size="sm"
                :disabled="!canAddCondition"
                @click="addCondition"
                class="ml-2 flex items-center justify-center p-0 align-middle !h-10 !w-10 !min-w-[40px]"
                style="
                  height: 38px;
                  width: 40px;
                  min-width: 40px;
                  margin-top: -9px;
                "
              >
                <template #icon>
                  <fluent-icon icon="add" size="18" />
                </template>
              </Button>
            </div>
          </div>

          <div class="flex justify-end">
            <Button
              variant="solid"
              color="blue"
              size="sm"
              :disabled="!newStage.name"
              @click.stop="handleAddStage"
              :label="editingStage ? 'Salvar' : 'Adicionar etapa'"
            />
          </div>
          </div>



        </div> <!-- Fim do conteúdo colapsável -->

        <!-- Campos Personalizados Globais - Grupo Colapse -->
        <div class="flex items-center justify-between mb-4" style="margin-top: 16px;">
          <div class="flex items-center gap-2 text-base font-medium">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-list-plus-icon lucide-list-plus">
              <path d="M16 5H3"/>
              <path d="M11 12H3"/>
              <path d="M16 19H3"/>
              <path d="M18 9v6"/>
              <path d="M21 12h-6"/>
            </svg>
            Campos Personalizados Globais
          </div>
          <div class="flex-1 mx-4 h-px bg-slate-200 dark:bg-slate-700"></div>
          <button
            type="button"
            @click="toggleGlobalFields"
            class="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            <fluent-icon
              :icon="globalFieldsCollapsed ? 'chevron-down' : 'chevron-up'"
              size="16"
            />
          </button>
        </div>

        <!-- Conteúdo colapsável dos campos globais -->
        <div v-show="!globalFieldsCollapsed">
          <div
            class="border border-slate-100 dark:border-slate-800 rounded-lg p-4 space-y-4"
          >
            <div class="space-y-4">
              <div
                v-for="(attr, idx) in globalCustomAttributes || []"
                :key="idx"
                class="flex gap-2 items-center"
              >
                <input
                  v-model="attr.name"
                  type="text"
                  class="w-1/2 rounded-lg"
                  placeholder="Chave (name)"
                />
                <select v-model="attr.type" class="w-1/2 rounded-lg">
                  <option value="" disabled selected>Tipo</option>
                  <option value="string">Texto</option>
                  <option value="number">Número</option>
                  <option value="date">Data</option>
                  <option value="boolean">Verdadeiro/Falso</option>
                </select>
                <select
                  v-model="attr.field_type"
                  class="px-2 py-1 border rounded text-sm w-36 h-10 align-middle"
                >
                  <option value="single">Único</option>
                  <option value="list">Lista</option>
                </select>
                <button
                  type="button"
                  @click="removeGlobalCustomAttribute(idx)"
                  class="p-1 text-ruby-500"
                >
                  ✕
                </button>
              </div>
              <button
                type="button"
                @click="addGlobalCustomAttribute"
                class="mt-2 px-3 py-1 bg-woot-500 text-white rounded"
              >
                Adicionar campo
              </button>
            </div>
          </div>
        </div>

      </div>

      <!-- Coluna Direita - Lista de Etapas -->
      <div
        class="space-y-6"
      >
        <div class="sticky top-0">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2 text-sm font-medium">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-columns3-icon lucide-columns-3">
                <rect width="18" height="18" x="3" y="3" rx="2"/>
                <path d="M9 3v18"/>
                <path d="M15 3v18"/>
              </svg>
              <span>Etapas desse funil</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="px-2 py-1 text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-full">
                {{ stages.length }}
              </span>
              <button
                type="button"
                @click="toggleStages"
                class="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <fluent-icon
                  :icon="stagesCollapsed ? 'chevron-down' : 'chevron-up'"
                  size="16"
                />
              </button>
            </div>
          </div>

          <!-- Lista de Etapas -->
          <div v-show="!stagesCollapsed" class="space-y-2">
            <draggable
              v-model="stages"
              :options="dragOptions"
              item-key="id"
              class="space-y-2"
              @change="handleOrderChange"
            >
              <template #item="{ element: stage, index }">
                <div
                  class="stage-preview-card flex flex-col border border-slate-100 dark:border-slate-800 rounded-lg cursor-move group transition-colors duration-200 bg-slate-100 dark:bg-slate-800"
                  @mouseenter="stage._hover = true"
                  @mouseleave="stage._hover = false"
                  :style="
                    stage._hover
                      ? {
                          background: `linear-gradient(90deg, ${stage.color}22 0%, transparent 100%)`,
                        }
                      : {}
                  "
                >
                  <div class="flex items-center gap-2 p-2 select-none">
                    <div
                      class="w-2.5 h-2.5 rounded-full"
                      :style="{ backgroundColor: stage.color }"
                    />
                    <div
                      class="text-sm font-medium flex-1 truncate"
                      :title="stage.name"
                    >
                      {{
                        stage.name.length > 25
                          ? stage.name.slice(0, 25) + '…'
                          : stage.name
                      }}
                    </div>
                    <div class="flex items-center gap-2">
                      <div class="relative">
                        <button
                          type="button"
                          class="text-slate-400 hover:text-slate-500 p-0 m-0"
                          @click.stop="copyStageId(stage.id)"
                        >
                          <fluent-icon icon="copy" size="14" />
                        </button>
                        <span
                          v-if="copiedStageId[stage.id]"
                          class="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 text-xs rounded bg-slate-700 text-white whitespace-nowrap z-10"
                          >Copiado!</span
                        >
                      </div>
                      <button
                        type="button"
                        class="text-xs text-slate-400 hover:text-slate-500 p-0 m-0"
                        @click.stop="startEditing(stage)"
                      >
                        <fluent-icon icon="edit" size="14" />
                      </button>
                      <button
                        type="button"
                        class="text-slate-400 hover:text-slate-500 p-0 m-0"
                        @click.stop="removeStage(index)"
                      >
                        <fluent-icon icon="delete" size="14" />
                      </button>
                    </div>
                  </div>
                  <div
                    class="stage-preview-details p-0 max-h-0 opacity-0 overflow-hidden transition-all duration-200 ease-in-out group-hover:px-3 group-hover:pb-3 group-hover:opacity-100 group-hover:max-h-[500px] group-hover:pointer-events-auto pointer-events-none"
                  >
                    <div class="text-sm text-slate-600 mb-2">
                      {{ stage.description }}
                    </div>

                    <!-- Estatísticas da Etapa -->
                    <div v-if="stageStats[stage.id]" class="mb-2 px-1">
                      <div class="grid grid-cols-2 gap-2 text-xs">
                        <div class="flex justify-between items-center">
                          <span class="bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-1.5 py-0.5 rounded text-xs font-medium">itens</span>
                          <span class="text-slate-700 dark:text-slate-200 font-medium">{{ stageStats[stage.id].count || 0 }}</span>
                        </div>
                        <div class="flex justify-between items-center">
                          <span class="bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-1.5 py-0.5 rounded text-xs font-medium">valor</span>
                          <span class="text-slate-700 dark:text-slate-200 font-medium">{{ formatCurrency(stageStats[stage.id].total_value || 0) }}</span>
                        </div>
                      </div>
                    </div>

                    <div
                      v-if="
                        stage.auto_create_conditions &&
                        stage.auto_create_conditions.length > 0
                      "
                      class="mt-1"
                    >
                      <div class="flex flex-wrap gap-1">
                        <span
                          v-for="(
                            condition, index
                          ) in stage.auto_create_conditions"
                          :key="index"
                          class="text-xs px-1 py-0.5 bg-slate-100 dark:bg-slate-700 rounded"
                        >
                          {{ getConditionTypeLabel(condition.type) }}:
                          {{ condition.value }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </draggable>

            <!-- Empty State -->
            <div
              v-if="!stages.length"
              class="flex flex-col items-center justify-center py-8 text-slate-400"
            >
              <fluent-icon icon="list" size="32" class="mb-2" />
              <p class="text-sm">
                {{ t('KANBAN.FUNNELS.FORM.STAGES.EMPTY') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Agentes do Funil -->
        <div
          class="rounded-lg space-y-4 mt-6"
        >
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2 text-sm font-medium">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-users-icon lucide-users">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                <path d="M16 3.128a4 4 0 0 1 0 7.744"/>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                <circle cx="9" cy="7" r="4"/>
              </svg>
              <span>Agentes do Funil</span>
            </div>
            <button
              type="button"
              @click="toggleAgents"
              class="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              <fluent-icon
                :icon="agentsCollapsed ? 'chevron-down' : 'chevron-up'"
                size="16"
              />
            </button>
          </div>
          <!-- Select com busca -->
          <div v-show="!agentsCollapsed" ref="agentSelectorRef" class="relative mb-2">
            <input
              v-model="agentSearch"
              type="text"
              class="w-full px-3 py-2 border rounded-lg"
              placeholder="Buscar agente..."
              @focus="openAgentDropdown"
              @input="openAgentDropdown"
            />
            <div
              v-if="agentDropdownOpen"
              class="absolute left-0 top-full w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-60 overflow-y-auto scrollbar-hide z-[99999]"
            >
              <div
                v-for="agent in filteredAgents"
                :key="agent.id"
                class="px-3 py-2 hover:bg-woot-50 dark:hover:bg-slate-700 cursor-pointer flex items-center gap-2"
                @click="addAgent(agent)"
              >
                <Avatar
                  :name="agent.name"
                  :src="agent.avatar_url"
                  :size="24"
                  :status="agent.availability_status"
                />
                <div class="flex items-center gap-2 flex-1">
                  <span class="text-sm">{{ agent.name }}</span>
                  <span class="px-2 py-0.5 text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-full capitalize">{{ agent.role }}</span>
                </div>
              </div>
              <div
                v-if="filteredAgents.length === 0"
                class="px-3 py-2 text-slate-400 text-sm"
              >
                Nenhum agente encontrado
              </div>
            </div>
          </div>
          <!-- Listagem compacta dos agentes selecionados -->
          <div v-if="formData.agents.length" v-show="!agentsCollapsed" class="space-y-1">
            <div
              class="flex items-center justify-between px-2 py-1 bg-slate-50 dark:bg-slate-800 rounded-lg mb-2"
            >
              <span class="text-sm text-slate-600 dark:text-slate-300">
                {{ formData.agents.length }} agente{{
                  formData.agents.length !== 1 ? 's' : ''
                }}
                selecionado{{ formData.agents.length !== 1 ? 's' : '' }}
              </span>
              <button
                type="button"
                class="text-xs text-ruby-500 hover:text-ruby-600 dark:text-ruby-400 dark:hover:text-ruby-300"
                @click="formData.agents = []"
              >
                Limpar seleção
              </button>
            </div>
            <div
              v-for="agent in formData.agents"
              :key="agent.id"
              class="flex items-center gap-2 px-2 py-1 border border-slate-100 dark:border-slate-800 rounded mb-1"
            >
              <Avatar
                :name="agent.name"
                :src="agent.avatar_url"
                :size="24"
                :status="agent.availability_status"
              />
              <div class="flex items-center gap-2 flex-1">
                <span class="text-sm">{{ agent.name }}</span>
                <span class="px-2 py-0.5 text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-full capitalize">{{ agent.role }}</span>
              </div>
              <button
                type="button"
                class="ml-auto text-xs text-ruby-500 hover:text-ruby-600"
                @click="removeAgent(agent)"
              >
                ✕
              </button>
            </div>
          </div>
        </div>

        <!-- Modelos de Mensagem -->
        <div
          class="mt-4 rounded-lg space-y-4"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2 text-sm font-medium">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-messages-square-icon lucide-messages-square">
                <path d="M16 10a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 14.286V4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
                <path d="M20 9a2 2 0 0 1 2 2v10.286a.71.71 0 0 1-1.212.502l-2.202-2.202A2 2 0 0 0 17.172 19H10a2 2 0 0 1-2-2v-1"/>
              </svg>
              <span>Modelos de Mensagem</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="px-2 py-1 text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-full">
                {{ getTotalMessageTemplates() }}
              </span>
              <button
                type="button"
                @click="toggleTemplates"
                class="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <fluent-icon
                  :icon="templatesCollapsed ? 'chevron-down' : 'chevron-up'"
                  size="16"
                />
              </button>
            </div>
          </div>

          <div v-show="!templatesCollapsed" class="space-y-2 scrollbar-hide">
            <div
              v-for="stage in stages"
              :key="stage.id"
              class="border border-slate-50 dark:border-slate-700 rounded p-2"
            >
              <div class="flex items-center gap-2 mb-1">
                <div
                  class="w-3 h-3 rounded-full"
                  :style="{ backgroundColor: stage.color }"
                />
                <span class="text-sm font-medium">{{ stage.name }}</span>
                <span class="text-xs text-slate-500">
                  ({{ (stage.message_templates || []).length }})
                </span>
              </div>

              <div v-if="(stage.message_templates || []).length > 0" class="space-y-1">
                <div
                  v-for="template in (stage.message_templates || []).slice(0, 2)"
                  :key="template.id"
                  class="text-xs p-1 bg-slate-50 dark:bg-slate-800 rounded flex justify-between items-center"
                >
                  <span class="truncate flex-1">{{ template.title }}</span>
                  <span class="text-slate-400 ml-2">{{ template.created_at ? new Date(template.created_at).toLocaleDateString() : '' }}</span>
                </div>
                <div
                  v-if="(stage.message_templates || []).length > 2"
                  class="text-xs text-slate-500 text-center"
                >
                  +{{ (stage.message_templates || []).length - 2 }} mais...
                </div>
              </div>

              <div v-else class="text-xs text-slate-400 italic">
                Nenhum template nesta etapa
              </div>
            </div>
          </div>

        </div>

        <!-- Status e Configurações -->
        <div class="mt-6 space-y-4">
          <!-- Histórico de Otimizações -->
          <div v-if="funnelMetadata.optimization_history?.length > 0" class="space-y-2">
            <label class="text-sm font-medium text-slate-700 dark:text-slate-300">
              Histórico de Otimizações
            </label>
            <div class="space-y-1">
              <div
                v-for="(optimization, index) in funnelMetadata.optimization_history.slice(-3)"
                :key="index"
                class="text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 px-2 py-1 rounded"
              >
                {{ new Date(optimization.date).toLocaleDateString() }} - {{ optimization.type }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<style lang="scss" scoped>
.border-woot {
  border-color: var(--color-woot);
  border-width: 1px;
}

.ghost-card {
  opacity: 0.5;
  background: #c8ebfb;
}

.cursor-move {
  cursor: move;
}

.loading-spinner {
  @apply w-6 h-6 border-2 border-slate-200 border-t-woot-500 rounded-full animate-spin;
}

.agent-card {
  @apply transition-all duration-200;

  &:hover {
    @apply transform scale-[1.02];
  }
}

// Oculta a scrollbar mas permite scroll
.scrollbar-hide {
  scrollbar-width: none !important; /* Firefox */
  -ms-overflow-style: none !important; /* IE 10+ */
}
.scrollbar-hide::-webkit-scrollbar {
  display: none !important; /* Chrome/Safari/Webkit */
}

/* Força ocultar scrollbar em todos os elementos */
.funnel-form {
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
}
.funnel-form::-webkit-scrollbar {
  display: none !important;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  max-height: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
  max-height: 500px;
}
.stage-preview-card {
  position: relative;
}
.stage-preview-card {
  background: var(--tw-bg-opacity, 1) var(--tw-gradient, none);
}
.stage-preview-card:hover {
  background: var(--tw-gradient);
}
.stage-preview-details {
  /* Remove bg e gradiente daqui, fica só no card externo */
}

// Estilos para o colapso vertical do grupo Dados Básicos
.collapsible-content {
  transition: all 0.3s ease-in-out;
}
</style>
